import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { GetPlayerAuctionDetails } from "../../redux/auth-reducer/auth-service";
import { useHubEvent } from "../../hooks/useHubEvent";
import { useHubResync } from "../../hooks/useHubResync";
import { mergePushedRows, preferNewerRows, syncTimerEnds, teamKey } from "../../utils/helper/auctionGrid";
import { RECHECK_AFTER_ZERO_MS } from "../../utils/helper/lotClock";
import {
  DETAIL_LOT_FIELDS,
  auctionStatusOf,
  defaultTeamId,
  readDetailsReply,
  runningLotOf,
  startsInDeadline,
  withPositions,
} from "../../utils/helper/auctionDetails";

/** After the first recheck at 00:00, how often the page asks again, and how many times in all. */
export const RECHECK_INTERVAL_MS = 2000;
export const RECHECK_ATTEMPTS = 4;

/** setTimeout fires at once past this (2^31 - 1 ms, about 24 days), so nothing is scheduled beyond it. */
const MAX_TIMEOUT_MS = 2147483647;

const EMPTY = { details: null, lots: [], clocks: {}, startsAt: null };

/**
 * Asks the server again a moment after a countdown reaches 00:00, then every 2 s, 4 times in all,
 * for as long as the countdown is the same one. The client never closes a lot or starts an auction
 * itself (#191): the Worker's push, 1.5 s after the end, usually answers first, and a new deadline
 * or a lot that changed stops the rechecks.
 */
const useRecheckAfter = (deadline, reload) => {
  useEffect(() => {
    if (!Number.isFinite(deadline)) return undefined;
    const delay = Math.max(0, deadline - Date.now()) + RECHECK_AFTER_ZERO_MS;
    if (delay > MAX_TIMEOUT_MS) return undefined;

    let attempts = 0;
    let timer = null;
    const check = () => {
      attempts += 1;
      reload.current();
      if (attempts < RECHECK_ATTEMPTS) timer = setTimeout(check, RECHECK_INTERVAL_MS);
    };
    timer = setTimeout(check, delay);
    return () => clearTimeout(timer);
  }, [deadline, reload]);
};

/**
 * The player's Auction Details page (#237): one auction with every primary lot
 * (Player/GetAuctionDetails), kept live without a refresh.
 *
 * - Read on mount, after a reconnect (useHubResync), and when the tab is shown again; only the
 *   newest reply is applied, and no reply keeps what is shown.
 * - The lot pushes (ReceiveBidStatus every 7 s and at a lot's end, ReceiveCurrentBid after a bid)
 *   are merged into this auction's lots, newest first (mergePushedRows); a lot missing from a push
 *   is unchanged. The pool, the bid counts and the auction's status follow from the lots.
 * - The running lot's clock and the "Starts in" countdown are server time (lotClock.js).
 * - The selected team is chosen once, at the first read (defaultTeamId), and stays on its team as
 *   it changes state.
 *
 * @param {number|string} auctionId The auction, from the URL.
 * @returns {object} The read, the lots, their clocks, the selection, and reload.
 */
export const useAuctionDetails = (auctionId) => {
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const requestRef = useRef(0);

  const reload = useCallback(async () => {
    const request = ++requestRef.current;
    const res = await GetPlayerAuctionDetails(auctionId);
    if (request !== requestRef.current) return;
    const receivedAt = Date.now();
    const reply = readDetailsReply(res);
    setLoading(false);
    if (!reply) return;
    if (reply.notFound) {
      setNotFound(true);
      return;
    }

    const lots = withPositions(reply.details.lots);
    setNotFound(false);
    setState((prev) => ({
      details: reply.details,
      // A push received while the read was on its way is newer than it (#191).
      lots: preferNewerRows(lots, prev.lots, DETAIL_LOT_FIELDS, teamKey),
      clocks: syncTimerEnds(prev.clocks, lots, receivedAt, { prune: true, keyOf: teamKey }),
      startsAt: startsInDeadline(reply.details, receivedAt),
    }));
    setSelectedId((prev) => prev ?? defaultTeamId(lots));
  }, [auctionId]);

  const reloadRef = useRef(reload);
  useLayoutEffect(() => {
    reloadRef.current = reload;
  });

  useEffect(() => {
    const requests = requestRef;
    reload();
    // Leaving the page: no reply may be written any more.
    return () => {
      requests.current += 1;
    };
  }, [reload]);

  // A hidden tab's timers are slowed and its pushes may be late: read again when it is shown.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") reloadRef.current();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Several pushes can arrive in one tick, so each is merged in a functional update.
  const applyPush = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const receivedAt = Date.now();
    setState((prev) => {
      const own = prev.details
        ? rows.filter((row) => Number(row?.auctionId) === Number(prev.details.auctionId))
        : [];
      if (own.length === 0) return prev;
      const lots = mergePushedRows(prev.lots, own, DETAIL_LOT_FIELDS, teamKey);
      const clocks = syncTimerEnds(prev.clocks, own, receivedAt, { keyOf: teamKey });
      return lots === prev.lots && clocks === prev.clocks ? prev : { ...prev, lots, clocks };
    });
  };

  useHubEvent("ReceiveBidStatus", applyPush);
  useHubEvent("ReceiveCurrentBid", applyPush);
  // Messages sent while the connection was down are lost.
  useHubResync(reload);

  const runningLot = runningLotOf(state.lots);
  const runningClock = runningLot ? state.clocks[teamKey(runningLot)] ?? null : null;
  const auctionStatus = state.details ? auctionStatusOf(state.details, state.lots) : null;

  useRecheckAfter(runningClock?.status === "Live" ? runningClock.endsAt : null, reloadRef);
  useRecheckAfter(auctionStatus === "Upcoming" ? state.startsAt : null, reloadRef);

  return {
    details: state.details,
    lots: state.lots,
    auctionStatus,
    runningLot,
    runningClock,
    startsAt: state.startsAt,
    loading,
    notFound,
    selectedId,
    select: setSelectedId,
    reload,
  };
};
